/**
 * Decoder for the columnar roster artifact produced by build-roster.mjs.
 *
 * Runs in both the browser (once, on first open, from the cached artifact) and
 * in the Worker (when replaying a match to verify a submitted result), so it
 * must stay dependency-free.
 *
 * The artifact stores players as flat tuples to keep the payload small; this
 * turns them back into objects once, at load time. 2,490 objects is trivial to
 * materialise and saves every call site from remembering the tuple order.
 */

import { ROLE_CODES, FLAG } from './overrides.mjs';

const ROLE_NAMES = Object.fromEntries(Object.entries(ROLE_CODES).map(([k, v]) => [v, k]));

/** Schema version this decoder understands. A mismatch means a stale cache. */
export const SUPPORTED_SCHEMA = 1;

/**
 * @param {object} artifact parsed roster.json
 * @returns {{seasons: Array, players: Map<string, object[]>, years: number[]}}
 */
export function decodeRoster(artifact) {
  if (artifact?.v !== SUPPORTED_SCHEMA) {
    throw new Error(
      `roster schema ${artifact?.v} is not supported (expected ${SUPPORTED_SCHEMA}) -- clear the cache`,
    );
  }

  const { names, cities } = artifact;

  /** name -> every season-squad that player appears in, for duplicate-blocking */
  const players = new Map();

  const seasons = artifact.seasons.map(([year, teams]) => {
    const decodedTeams = teams.map(([cityIdx, rows]) => {
      const city = cities[cityIdx];
      const squad = rows.map(([nameIdx, role, bat, pow, bowl, posMin, posMax, flags, bowlOvers]) => {
        const name = names[nameIdx];
        const player = {
          name,
          role: ROLE_NAMES[role],
          bat,
          pow,
          bowl,
          // Batting-order range this player is eligible for. The draft assigns
          // one specific slot inside it and then locks that slot.
          posMin,
          posMax,
          // Most overs this player bowled in a single match that season,
          // observed from real ball-by-ball data. Decides bowling eligibility;
          // the TYPE/role label does not (see build-roster.mjs).
          bowlOvers,
          canBowl: bowlOvers >= 1,
          isFrontlineBowler: bowlOvers >= 3,
          isIndian: (flags & FLAG.INDIAN) !== 0,
          isOrangeCap: (flags & FLAG.ORANGE_CAP) !== 0,
          isPurpleCap: (flags & FLAG.PURPLE_CAP) !== 0,
          year,
          city,
          // Stable identity for the no-duplicates rule: the same player drafted
          // from a different season is still the same player.
          id: name,
          // Unique key for a specific season-version of a player.
          cardId: `${name}|${year}`,
        };
        if (!players.has(name)) players.set(name, []);
        players.get(name).push(player);
        return player;
      });
      return { city, year, label: `${city} ${year}`, squad };
    });
    return { year, teams: decodedTeams };
  });

  return {
    seasons,
    players,
    years: seasons.map((s) => s.year),
  };
}

/**
 * All squads whose season falls inside an inclusive year range. This is the
 * pool the draft spins through; the user fixes the range before drafting and
 * cannot change it mid-session.
 */
export function squadsInRange(decoded, fromYear, toYear) {
  if (fromYear > toYear) throw new Error(`invalid year range ${fromYear}-${toYear}`);
  const out = [];
  for (const season of decoded.seasons) {
    if (season.year >= fromYear && season.year <= toYear) out.push(...season.teams);
  }
  if (out.length === 0) throw new Error(`no squads in range ${fromYear}-${toYear}`);
  return out;
}
